import {
  EnvConfig,
  LIST_MENTIONABLE_USERS,
  useCurrentWorkspace,
  useCustomQuery,
} from '@gripp/shared-logic';
import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import {
  MentionInput,
  MentionSuggestionsProps,
  parseValue,
} from 'react-native-controlled-mentions';
import { Portal } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { AccountAvatar } from '../accountAvatar';

type ConditionalWrapperProps = {
  children: ReactNode;
};

export type MentionInputFieldMobileProps = {
  inputFieldText: string;
  setInputFieldText: (text: string) => void;
  setIsMultiline: (value: boolean) => void;
  inputHeight: number;
  setInputHeight: (value: number) => void;
  suggestionsPosition?: 'top' | 'bottom';
  placeholder?: string;
};

export const MentionsInputFieldMobile = (
  props: MentionInputFieldMobileProps
) => {
  const { t } = useTranslation();
  const {
    inputFieldText,
    setInputFieldText,
    setIsMultiline,
    inputHeight,
    setInputHeight,
    suggestionsPosition = 'top',
    placeholder,
  } = props;

  const isAndroid = Platform.OS === 'android';
  const workspace = useCurrentWorkspace();

  const [search, setSearch] = useState('');

  // Ref holding the keyword being typed after @. Set synchronously during
  // renderWorkspaceUsers (render phase) so it is always current, then flushed
  // to the `search` state via a useEffect to avoid calling setState during
  // render (a React anti-pattern that can cause batching surprises).
  const activeKeywordRef = useRef<string | null>(null);

  // Sync the active keyword to the search query state after every render where
  // the input text changed — the same condition that changes the keyword.
  useEffect(() => {
    const kw = activeKeywordRef.current;
    if (kw !== null) {
      setSearch(kw);
    }
  }, [inputFieldText]);

  // Ref holding the plain-text substring that precedes the active @.
  // Updated synchronously inside renderWorkspaceUsers (render phase) so the
  // second hidden mirror TextInput always reads the current value in the same
  // render cycle — no extra state update needed to get the value into the DOM.
  const textBeforeAtRef = useRef('');
  // Measured height (px) of the content in textBeforeAtRef — reported by the
  // second hidden mirror TextInput via onContentSizeChange.
  const [heightBeforeAt, setHeightBeforeAt] = useState(0);

  const handleBeforeAtContentSizeChange = useCallback((contentHeight: number) => {
    setHeightBeforeAt(Math.floor(contentHeight));
  }, []);

  const { data: workspaceUsers } = useCustomQuery({
    query: LIST_MENTIONABLE_USERS,
    cacheKey: ['mentionableUsers', workspace?.id, search],
    variables: {
      filter: {
        workspaceId: workspace?.id,
        keyword: search,
      },
    },
    options: {
      enabled: !!workspace?.id,
    },
  });

  const messageInputRef = useRef(null);

  // Stable part-types array used only to extract plain text for the mirror input.
  const mirrorPartTypes = useMemo(() => [{ trigger: '@' as const }], []);

  // Convert encoded mention value (e.g. "@[John](id)") to visible plain text
  // so the hidden mirror TextInput wraps lines at exactly the same positions.
  const { plainText } = useMemo(
    () => parseValue(inputFieldText, mirrorPartTypes),
    [inputFieldText, mirrorPartTypes]
  );

  const getInputDynamicHeight = useCallback((contentHeight: number) => {
    const contentHeightFloor = Math.floor(contentHeight);

    const newHeight =
      contentHeightFloor >= inputLineHeight
        ? contentHeightFloor
        : inputLineHeight;

    const newInputHeight =
      newHeight >= maxHeightInputContainer
        ? maxHeightInputContainer
        : newHeight + inputLineHeight;

    return { newInputHeight, contentHeightFloor };
  }, []);

  const handleContentSizeChange = useCallback((contentHeight: number) => {
    const { newInputHeight, contentHeightFloor } =
      getInputDynamicHeight(contentHeight);
    setInputHeight(newInputHeight);

    setIsMultiline(contentHeightFloor >= multilineThreshold);
  }, []);

  const MentionsWrapper: React.FC<ConditionalWrapperProps> = ({ children }) => {
    return isAndroid ? <Portal>{children}</Portal> : <>{children}</>;
  };

  const renderWorkspaceUsers: FC<MentionSuggestionsProps> = ({
    keyword,
    onSuggestionPress,
  }) => {
    if (keyword == null) {
      activeKeywordRef.current = null;
      // Reset so the mirror TextInput collapses back to empty between sessions.
      textBeforeAtRef.current = '';
      // Clear search state to close the previous mention query subscription
      // and avoid rendering stale suggestions on the next @ interaction.
      setSearch('');
      return null;
    }

    // Store keyword in a ref; the useEffect above flushes it to `search` state
    // after the render, avoiding a setState-during-render anti-pattern.
    activeKeywordRef.current = keyword;

    // Locate the active @ in the ENCODED text, not in plainText.
    // Completed mentions are stored as @[Name](id) — they start with @[ so they
    // can never match @keyword (which starts with a letter/digit).  This prevents
    // lastIndexOf from latching onto a completed @Johnny when the user types @Jo.
    const atIndexEncoded = inputFieldText.lastIndexOf('@' + keyword);

    if (atIndexEncoded !== -1) {
      // Convert only the encoded prefix (everything before the @) to plain text
      // so the measurement mirror sees the same character widths as the input.
      const { plainText: plainBeforeAt } = parseValue(
        inputFieldText.substring(0, atIndexEncoded),
        mirrorPartTypes
      );
      textBeforeAtRef.current = plainBeforeAt;
    } else {
      textBeforeAtRef.current = '';
    }

    // Position the menu just above the line where @ was typed.
    // heightBeforeAt = measured content height of textBeforeAtRef (lines above @).
    // inputHeight - heightBeforeAt gives the distance from the bottom of the
    // input to the top of the @ line.  Add a small gap so the menu doesn't
    // butt up against the text.  Clamp so the menu is never below inputLineHeight
    // above the container bottom (safety guard for edge cases).
    const menuBottom =
      suggestionsPosition === 'top'
        ? Math.max(
            inputHeight - heightBeforeAt + menuGap,
            inputLineHeight + menuGap
          )
        : undefined;

    const menuTop =
      suggestionsPosition === 'bottom' ? inputHeight + menuGap : undefined;

    return (
      <View
        style={[
          styles.mentionsContainer,
          {
            position: 'absolute',
            left: 0,
            right: -suggestionRightOverflow,
            zIndex: 1000,
            bottom: menuBottom,
            top: menuTop,
            backgroundColor: Colors.neutral[200],
          },
        ]}
      >
        <ScrollView
          style={styles.mentionsScrollViewContainer}
          keyboardShouldPersistTaps={'always'}
          nestedScrollEnabled={true}
        >
          {workspaceUsers?.account?.listMentionableUsers?.items.map((user) => (
            <Pressable
              key={user.id}
              onPress={() => {
                onSuggestionPress({
                  id: user?.id,
                  name: `${user?.name?.first} ${user?.name?.last}`,
                });
              }}
              style={styles.mentionItemRow}
            >
              <AccountAvatar
                avatar={user.avatar}
                mediaUrl={EnvConfig.mediaUrl}
                size={36}
              />
              <View style={{ marginLeft: 16 }}>
                <Text style={styles.mentionName}>
                  {user.name?.first} {user.name?.last}
                </Text>
                <Text style={styles.mentionEmail}>{user.email}</Text>
              </View>
            </Pressable>
          ))}
        </ScrollView>
      </View>
    );
  };

  return (
    <View style={styles.mentionInputField}>
      {/*
       * Hidden mirror TextInput — used solely for height measurement.
       *
       * Why: react-native-controlled-mentions renders its inner TextInput with
       * a <Text> child for mention highlighting. On iOS, a TextInput that has
       * children does not fire onContentSizeChange reliably, so we cannot use
       * the library's component to measure height.
       *
       * Fix: this plain TextInput has NO children, so onContentSizeChange fires
       * correctly on every platform. It mirrors the plain-text content (mentions
       * expanded to "@Name") so line-wrapping matches the visible input exactly.
       * scrollEnabled={false} lets it render at its natural content height, which
       * makes the reported contentSize equal to the true intrinsic text height.
       * It is absolutely positioned and invisible so it has no visual effect.
       */}
      <TextInput
        style={[styles.messageInput, styles.hiddenMirrorInput]}
        multiline={true}
        scrollEnabled={false}
        editable={false}
        value={plainText}
        onContentSizeChange={(e) =>
          handleContentSizeChange(e.nativeEvent.contentSize.height)
        }
      />
      {/*
       * Second hidden mirror — measures the height of text BEFORE the active @.
       * The ref value is set synchronously during renderWorkspaceUsers (same
       * render cycle), so this TextInput always receives the up-to-date substring.
       * Its reported contentSize.height tells us how many lines sit above the @,
       * which we use to anchor the suggestion menu right above the caret line.
       */}
      <TextInput
        style={[styles.messageInput, styles.hiddenMirrorInput]}
        multiline={true}
        scrollEnabled={false}
        editable={false}
        value={textBeforeAtRef.current}
        onContentSizeChange={(e) =>
          handleBeforeAtContentSizeChange(e.nativeEvent.contentSize.height)
        }
      />
      <MentionInput
        inputRef={messageInputRef}
        value={inputFieldText}
        onChange={setInputFieldText}
        placeholder={placeholder || t('common.typeMessage')}
        placeholderTextColor={Colors.grayDisabled}
        autoFocus={false}
        // Height is driven by inputHeight state, which is updated by the hidden
        // mirror TextInput's onContentSizeChange handler above.
        style={[styles.messageInput, { height: inputHeight }]}
        multiline={true}
        numberOfLines={3}
        cursorColor={Colors.primaryDark}
        containerStyle={styles.mentionInputFieldInner}
        partTypes={[
          {
            trigger: '@',
            renderSuggestions: renderWorkspaceUsers,
            textStyle: styles.mentionText,
            isInsertSpaceAfterMention: true,
          },
        ]}
      />
    </View>
  );
};

const inputLineHeight = 18;
const maxLinesInput = 10;
const maxHeightInputContainer = maxLinesInput * inputLineHeight;
const multilineThreshold = 36;
// Gap (px) between the bottom of the suggestion menu and the top of the @ line.
const menuGap = 25;
// How far (px) the suggestion panel extends to the right past the input edge,
// covering the send-button area: close (32) + gap (8) + send (32) + paddingRight (12).
const suggestionRightOverflow = 84;

const styles = StyleSheet.create({
  // Outer wrapper — owns flex: 1 so it fills the inputContainer in issueFooter.
  mentionInputField: {
    flex: 1,
    width: '100%',
  },
  // Passed as containerStyle to the library's MentionInput (its internal View).
  mentionInputFieldInner: {
    flex: 1,
    width: '100%',
  },
  // Hidden TextInput used purely for height measurement. Absolutely positioned
  // so it does not disturb the layout of its sibling MentionInput.
  hiddenMirrorInput: {
    position: 'absolute',
    opacity: 0,
    // No explicit height — scrollEnabled={false} allows the TextInput to render
    // at its intrinsic content height, giving onContentSizeChange accurate data.
  },
  mentionText: {
    color: Colors.primaryDark,
    fontWeight: '400',
  },
  messageInput: {
    // Shared base style for both the hidden mirror input and the visible MentionInput.
    // The visible MentionInput additionally receives { height: inputHeight } inline.
    width: '100%',
    paddingLeft: 8,
    paddingTop: 8,
    paddingBottom: 8,
    paddingRight: 0,
    fontSize: 16,
    textAlignVertical: 'center',
    textAlign: 'left',
    ...Platform.select({
      ios: {
        paddingTop: 16,
        paddingBottom: 4,
      },
      web: {
        paddingTop: 12,
        paddingBottom: 12,
        outlineStyle: 'none',
        height: 67,
      },
    }),
  },
  mentionsContainer: {
    maxHeight: 216,
    backgroundColor: Colors.white,
    borderRadius: 4,
    shadowColor: Colors.black,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.35,
    shadowRadius: 5,
    elevation: 10,
  },
  mentionsScrollViewContainer: {
    borderRadius: 4,
    backgroundColor: Colors.white,
  },
  mentionItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 56,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  mentionName: {
    fontSize: 14,
    fontWeight: '400',
    letterSpacing: -0.25,
    color: Colors.mentionName,
  },
  mentionEmail: {
    fontSize: 12,
    fontWeight: '400',
    letterSpacing: 0.2,
    color: Colors.mentionEmail,
  },
});
